import { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { getTransformVideos } from "store/core/transformVideos/actions";

import { TransformVideosContext } from "./context";

const useStorageOperations = () => {
  const getTransformVideos = () => JSON.parse(localStorage.getItem("transformVideos") || "{}");
  const setTransformVideos = (transformVideos) =>
    localStorage.setItem("transformVideos", JSON.stringify(transformVideos));
  const getEntityId = () => localStorage.getItem("entityId");

  return { getTransformVideos, setTransformVideos, getEntityId };
};

export const TransformVideosProvider = ({ children, getTransformVideos }) => {
  const storageOperations = useStorageOperations();
  const initialTransformVideos = useMemo(() => storageOperations.getTransformVideos(), [storageOperations]);

  const [transformVideos, setTransformVideos] = useState(initialTransformVideos);
  const [streamIds, setStreamIds] = useState([]);

  const [isTransformListVisible, setIsTransformListVisible] = useState(false);

  const toggleTransformList = useCallback(() => {
    setIsTransformListVisible((prev) => !prev);
  }, []);

  const addTransformVideo = useCallback(
    ({ title, data }) => {
      const _transformVideos = {
        ...transformVideos,
        [data.requestId]: {
          requestId: data.requestId,
          title,
          format: data?.conversionAttributes?.output_format_type,
          progress: 0,
          status: "INITIATED",
          streamId: data?.streamId,
        },
      };
      const _streamIds = Array.from(new Set([...streamIds, data?.streamId]));

      setTransformVideos(_transformVideos);
      setStreamIds(_streamIds);
      storageOperations.setTransformVideos(_transformVideos);
      setIsTransformListVisible(true);
    },
    [storageOperations, transformVideos, streamIds],
  );

  const updateTransformVideo = useCallback(
    ({ requestId, data }) => {
      const _transformVideos = {
        ...transformVideos,
        [requestId]: {
          ...transformVideos[requestId],
          ...data,
        },
      };

      const tempTransformVideos = { ..._transformVideos };
      setTransformVideos(tempTransformVideos);
      if (data?.status === "COMPLETED" || data?.status === "FAILED") {
        delete _transformVideos[requestId];
        const _streamIds = Array.from(
          new Set(Object.keys(_transformVideos).map((key) => _transformVideos[key].streamId)),
        );
        setStreamIds(_streamIds);
      }

      storageOperations.setTransformVideos(_transformVideos);
    },
    [storageOperations, transformVideos],
  );

  const handleGetTransformVideo = useCallback(
    async (transformVideo, entityId) => {
      const streamId = transformVideo?.streamId;
      const payload = {
        params: {
          requestId: transformVideo?.requestId,
        },
        headers: {
          entityid: entityId,
        },
      };
      const response = await getTransformVideos({ streamId, entityId, payload });
      const _data = response?.payload?.data?.data?.[0];

      const data = {
        progress: _data?.progress,
        status: _data?.status,
      };
      updateTransformVideo({ requestId: transformVideo?.requestId, data });
    },
    [getTransformVideos, updateTransformVideo],
  );

  const handleStoredTransformVideos = useCallback(async () => {
    const savedTransformVideos = storageOperations.getTransformVideos();
    const entityId = storageOperations.getEntityId();
    const transformVideoPromises = Object.values(savedTransformVideos).map((transformVideo) =>
      handleGetTransformVideo(transformVideo, entityId),
    );
    await Promise.all(transformVideoPromises);
  }, [handleGetTransformVideo, storageOperations]);

  const handleCleanUpTransformVideos = useCallback(() => {
    setTransformVideos({});
    setStreamIds([]);
    storageOperations.setTransformVideos({});
  }, [storageOperations]);

  useEffect(() => {
    handleStoredTransformVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TransformVideosContext.Provider
      value={{
        transformVideos,
        setTransformVideos,
        isTransformListVisible,
        toggleTransformList,
        addTransformVideo,
        updateTransformVideo,
        streamIds,
        handleCleanUpTransformVideos,
      }}
    >
      {children}
    </TransformVideosContext.Provider>
  );
};

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = { getTransformVideos };

export default connect(mapStateToProps, mapDispatchToProps)(TransformVideosProvider);
